import React, { useState, useEffect } from 'react';
import { Select, Input, InputNumber, Radio, Checkbox, DatePicker,
         Button, Card, Tag, Progress, Spin, Alert, message } from 'antd';
import { SendOutlined, SaveOutlined, CheckCircleOutlined,
         PlusOutlined, MinusOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import coaImg  from '../../pages/assets/footer/coa.png';
import flagImg from '../../pages/assets/footer/flag.png';

const { TextArea } = Input;
const { Option }   = Select;

const API = window.location.hostname.includes('qoctesting')
    ? '/qoctesting/api/'
    : '/qoc/api/';

export default function QoCCollectorForm({ match }) {
    const token = match?.params?.token;

    const [data,         setData]         = useState(null);
    const [answers,      setAnswers]       = useState({});
    const [facilityId,   setFacilityId]   = useState(null);
    const [section,      setSection]      = useState(0);
    const [loading,      setLoading]      = useState(true);
    const [submitting,   setSubmitting]   = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [submitted,    setSubmitted]    = useState(false);
    const [submissions,  setSubmissions]  = useState([]);
    const [nextFacility, setNextFacility] = useState(null);
    const [error,        setError]        = useState(null);
    // repeatCounts: { [groupLabel]: activeInstanceCount }
    const [repeatCounts, setRepeatCounts] = useState({});

    useEffect(() => {
        axios.get(API + 'survey/collect/' + token)
            .then(r => {
                if (r.data.code !== 200) { setError(r.data.message); return; }
                setData(r.data.data);
                const draft = localStorage.getItem('collect_draft_' + token);
                if (draft) {
                    const d = JSON.parse(draft);
                    setAnswers(prev => ({ ...prev, ...(d.answers || {}) }));
                    setFacilityId(d.facility_id || null);
                    setRepeatCounts(d.repeatCounts || {});
                }
            })
            .catch(() => setError('Unable to load survey. Please check your link.'))
            .finally(() => setLoading(false));
    }, [token]); // eslint-disable-line

    // Whenever a facility becomes selected (auto-restored from a local
    // draft, or chosen manually), fetch whatever has ALREADY been saved
    // server-side for THIS facility's shared draft response -- combining
    // every collector's prior work -- and layer it underneath whatever is
    // currently in local state, so a fresher local edit always wins but
    // nothing another collector already saved is ever lost.
    useEffect(() => {
        if (!facilityId || !token) return;
        axios.get(API + 'survey/collect/' + token + '/facility/' + facilityId + '/answers')
            .then(r => {
                if (r.data.code !== 200) return;
                const serverAnswers = r.data.data?.answers || {};
                setAnswers(prev => ({ ...serverAnswers, ...prev }));
            })
            .catch(() => { });
    }, [facilityId, token]);

    const setAnswer = (qid, val) => {
        const a = { ...answers, [qid]: val };
        setAnswers(a);
        localStorage.setItem('collect_draft_' + token,
            JSON.stringify({ answers: a, facility_id: facilityId, repeatCounts }));
    };

    const setRepeatCount = (groupLabel, count, maxCount) => {
        const newCounts = { ...repeatCounts, [groupLabel]: Math.max(1, Math.min(count, maxCount)) };
        setRepeatCounts(newCounts);
        localStorage.setItem('collect_draft_' + token,
            JSON.stringify({ answers, facility_id: facilityId, repeatCounts: newCounts }));
    };

    const saveDraft = async () => {
        if (!facilityId) { message.warning('Please select your facility first'); return; }
        setSaving(true);
        try {
            const params = new URLSearchParams();
            params.append('facility_id', facilityId);
            Object.entries(answers).forEach(([k, v]) =>
                params.append(`answers[${k}]`,
                    (Array.isArray(v) || (v !== null && typeof v === 'object'))
                        ? JSON.stringify(v) : v));
            const r = await axios.post(API + 'survey/collect/' + token + '/submit', params);
            if (r.data.code !== 200) throw new Error(r.data.message);
            message.success('Draft saved — you can return and continue later');
        } catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    const submit = async () => {
        if (!facilityId) { message.warning('Please select your facility'); return; }
        const required = currentQuestions.filter(q =>
            q.required && shouldShow(q) && answers[q.id] === undefined);
        if (required.length) {
            message.warning(`Please answer ${required.length} required question(s)`);
            return;
        }
        setSubmitting(true);
        try {
            const params = new URLSearchParams();
            params.append('facility_id', facilityId);
            params.append('submit', '1');
            Object.entries(answers).forEach(([k, v]) =>
                params.append(`answers[${k}]`,
                    (Array.isArray(v) || (v !== null && typeof v === 'object'))
                        ? JSON.stringify(v) : v));
            const r = await axios.post(API + 'survey/collect/' + token + '/submit', params);
            if (r.data.code !== 200) throw new Error(r.data.message);
            localStorage.removeItem('collect_draft_' + token);
            const facName = data?.facilities?.find(f =>
                String(f.id) === String(facilityId))?.business_name || 'Facility';
            setSubmissions(prev => [...prev, {
                facility_id: facilityId, facility_name: facName,
                submitted_at: new Date().toLocaleString()
            }]);
            setSubmitted(true);
        } catch(e) { message.error(e.message || 'Submission failed'); }
        finally { setSubmitting(false); }
    };

    // ── Skip logic ────────────────────────────────────────────────────────────
    const shouldShow = (q) => {
        if (!q.skip_logic?.field) return true;
        const { field, operator, value } = q.skip_logic;
        const allQ = data?.survey?.questions || [];
        const refQ = allQ.find(qq =>
            (qq.xlsform_name && qq.xlsform_name === field) ||
            String(qq.id) === String(field)
        );
        if (!refQ) return true;
        const refAnswer  = String(answers[refQ.id] || '').toLowerCase();
        const compareVal = String(value || '').toLowerCase();
        if (operator === '=')        return refAnswer === compareVal;
        if (operator === '!=')       return refAnswer !== compareVal;
        if (operator === 'contains') return refAnswer.includes(compareVal);
        return true;
    };

    // ── Question renderer ─────────────────────────────────────────────────────
    const renderQ = (q) => {
        const val = answers[q.id];
        switch (q.type) {
            case 'text':
                return <Input value={val||''} onChange={e=>setAnswer(q.id,e.target.value)} placeholder="Type your answer..."/>;
            case 'textarea':
                return <TextArea rows={3} value={val||''} onChange={e=>setAnswer(q.id,e.target.value)}/>;
            case 'number':
                return <InputNumber value={val} onChange={v=>setAnswer(q.id,v)} style={{width:'100%'}}/>;
            case 'single':
                return (
                    <Radio.Group value={val} onChange={e=>setAnswer(q.id,e.target.value)}
                        style={{display:'flex',flexDirection:'column',gap:8}}>
                        {(q.options||[]).map(opt=>(
                            <Radio key={opt} value={opt}
                                style={{padding:'10px 14px',border:'1px solid '+(val===opt?'#1890ff':'#e5e7eb'),
                                    borderRadius:8,background:val===opt?'#eff6ff':'#fff'}}>
                                {opt}
                            </Radio>
                        ))}
                    </Radio.Group>
                );
            case 'multiple':
                return (
                    <Checkbox.Group value={val||[]} onChange={v=>setAnswer(q.id,v)}
                        style={{display:'flex',flexDirection:'column',gap:8}}>
                        {(q.options||[]).map(opt=>(
                            <Checkbox key={opt} value={opt}
                                style={{padding:'10px 14px',border:'1px solid '+((val||[]).includes(opt)?'#1890ff':'#e5e7eb'),
                                    borderRadius:8,background:(val||[]).includes(opt)?'#eff6ff':'#fff'}}>
                                {opt}
                            </Checkbox>
                        ))}
                    </Checkbox.Group>
                );
            case 'dropdown':
                return (
                    <Select value={val} onChange={v=>setAnswer(q.id,v)} placeholder="Select..."
                        style={{width:'100%'}} allowClear showSearch optionFilterProp="children">
                        {(q.options||[]).map(opt=><Option key={opt} value={opt}>{opt}</Option>)}
                    </Select>
                );
            case 'scale':
                return (
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {Array.from({length:(q.scale_max-q.scale_min)+1},(_,i)=>i+q.scale_min).map(n=>(
                            <button key={n} onClick={()=>setAnswer(q.id,n)}
                                style={{width:52,height:52,borderRadius:10,
                                    border:'2px solid '+(val===n?'#1890ff':'#e5e7eb'),
                                    background:val===n?'#1890ff':'#fff',
                                    color:val===n?'#fff':'#555',
                                    fontWeight:800,fontSize:18,cursor:'pointer'}}>
                                {n}
                            </button>
                        ))}
                    </div>
                );
            case 'boolean':
                return (
                    <div style={{display:'flex',gap:12}}>
                        {['Yes','No'].map(opt=>(
                            <button key={opt} onClick={()=>setAnswer(q.id,opt)}
                                style={{flex:1,padding:'14px 0',borderRadius:10,
                                    border:'2px solid '+(val===opt?'#1890ff':'#e5e7eb'),
                                    background:val===opt?'#1890ff':'#fff',
                                    color:val===opt?'#fff':'#555',
                                    fontWeight:700,fontSize:16,cursor:'pointer'}}>
                                {opt==='Yes'?'✅ Yes':'❌ No'}
                            </button>
                        ))}
                    </div>
                );
            case 'matrix': {
                const matCols = Array.isArray(q.columns) ? q.columns
                    : (typeof q.columns==='string' ? (() => { try { return JSON.parse(q.columns); } catch(e) { return ['Yes','No']; } })() : ['Yes','No']);
                const matRows = Array.isArray(q.rows) ? q.rows
                    : (typeof q.rows==='string' ? (() => { try { return JSON.parse(q.rows); } catch(e) { return []; } })() : []);
                return (
                    <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                            <thead>
                                <tr>
                                    <th style={{padding:'8px 12px',textAlign:'left',background:'#f8fafc',border:'1px solid #e2e8f0',width:'40%'}}></th>
                                    {matCols.map(col=>(
                                        <th key={col} style={{padding:'8px 12px',textAlign:'center',background:'#f8fafc',border:'1px solid #e2e8f0',fontWeight:600,color:'#374151'}}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matRows.map((row,ri)=>(
                                    <tr key={ri} style={{background:ri%2===0?'#fff':'#f8fafc'}}>
                                        <td style={{padding:'8px 12px',border:'1px solid #e2e8f0',fontWeight:500,color:'#374151'}}>{row}</td>
                                        {matCols.map(col=>(
                                            <td key={col} style={{padding:'8px 12px',textAlign:'center',border:'1px solid #e2e8f0'}}>
                                                <input type="radio"
                                                    name={`matrix_${q.id}_${ri}`} value={col}
                                                    checked={(val?.[row]||'')===col}
                                                    onChange={()=>setAnswer(q.id,{...(val||{}),[row]:col})}
                                                    style={{width:18,height:18,cursor:'pointer',accentColor:'#1890ff'}}/>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }
            case 'date':
                return <DatePicker value={val ? moment(val) : undefined} onChange={v=>setAnswer(q.id, v ? v.format('YYYY-MM-DD') : '')} style={{width:'100%'}}/>;
            default:
                return <Input value={val||''} onChange={e=>setAnswer(q.id,e.target.value)}/>;
        }
    };

    // ── Repeat group renderer ─────────────────────────────────────────────────
    // Groups questions that share the same repeat_group label, renders them as
    // an expandable/collapsible set of instances with + / - controls.
    const renderRepeatGroup = (groupLabel, groupQuestions, secQuestions) => {
        // Find the section definition so we know the max
        const secDef = data?.survey?.sections?.find(s => s.is_repeat && s.title === groupLabel);
        const maxCount  = 50; // User-controlled via the form's own +/- buttons, not a per-section setup limit
        const activeCount = repeatCounts[groupLabel] || 1;

        // groupQuestions are already filtered to this group; they're organised as
        // instance 1 fields, instance 2 fields, … instance maxCount fields.
        // We have one entry per (field × instance), so we pull unique field labels
        // from instance 1 to know the field list.
        const instance1Qs = groupQuestions.filter(q => q.repeat_instance === 1);
        const fieldNames  = instance1Qs.map(q => q.xlsform_name);

        // For a given instance number, return the matching question objects
        const getInstanceQs = (i) =>
            fieldNames.map(name =>
                groupQuestions.find(q => q.xlsform_name === name && q.repeat_instance === i)
            ).filter(Boolean);

        return (
            <Card key={groupLabel} size="small" style={{marginBottom:16,
                border:'2px solid #7c3aed',borderRadius:10,overflow:'hidden'}}>
                {/* Group header */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                    marginBottom:12,padding:'8px 12px',background:'#f5f3ff',
                    borderRadius:6,border:'1px solid #e9d5ff'}}>
                    <div>
                        <span style={{fontWeight:700,fontSize:14,color:'#7c3aed'}}>
                            👥 {groupLabel}
                        </span>
                        <span style={{fontSize:12,color:'#888',marginLeft:8}}>
                            ({activeCount} of {maxCount} entr{activeCount===1?'y':'ies'})
                        </span>
                    </div>
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        <Button size="small" icon={<MinusOutlined/>}
                            disabled={activeCount <= 1}
                            onClick={() => setRepeatCount(groupLabel, activeCount - 1, maxCount)}
                            style={{borderColor:'#7c3aed',color:'#7c3aed'}}>
                            Remove
                        </Button>
                        <Button size="small" icon={<PlusOutlined/>}
                            disabled={activeCount >= maxCount}
                            onClick={() => setRepeatCount(groupLabel, activeCount + 1, maxCount)}
                            type="primary"
                            style={{background:'#7c3aed',borderColor:'#7c3aed'}}>
                            Add
                        </Button>
                    </div>
                </div>

                {/* Render each active instance */}
                {Array.from({length: activeCount}, (_, idx) => {
                    const i      = idx + 1;
                    const instQs = getInstanceQs(i);
                    return (
                        <div key={i} style={{
                            marginBottom: i < activeCount ? 16 : 0,
                            padding:'12px',
                            background: i % 2 === 0 ? '#faf5ff' : '#fff',
                            borderRadius:8,
                            border:'1px solid #e9d5ff'
                        }}>
                            <div style={{fontWeight:700,fontSize:12,color:'#7c3aed',
                                marginBottom:10,textTransform:'uppercase',letterSpacing:0.5}}>
                                Entry {i}
                            </div>
                            {instQs.map(q => (
                                shouldShow(q) && (
                                    <div key={q.id} style={{marginBottom:12}}>
                                        <div style={{marginBottom:6}}>
                                            <span style={{fontSize:13,fontWeight:600,color:'#374151'}}>
                                                {/* Strip the "GroupLabel N — " prefix for cleaner display */}
                                                {q.label.replace(new RegExp(`^${groupLabel}\\s+${i}\\s+—\\s+`), '')}
                                            </span>
                                            {q.required && i === 1 &&
                                                <Tag color="red" style={{marginLeft:6,fontSize:10}}>Required</Tag>}
                                            {q.helper_text &&
                                                <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>{q.helper_text}</div>}
                                        </div>
                                        {renderQ(q)}
                                    </div>
                                )
                            ))}
                        </div>
                    );
                })}
            </Card>
        );
    };

    // ── Render question list for current section ───────────────────────────────
    // Handles both normal questions and repeat groups, avoiding duplicate group renders.
    const renderQuestionList = (questions) => {
        const rendered = new Set(); // track rendered repeat group labels
        let normalIdx  = 0;
        return questions.filter(q => shouldShow(q)).map((q) => {
            if (q.repeat_group) {
                if (rendered.has(q.repeat_group)) return null; // already rendered
                rendered.add(q.repeat_group);
                const groupQs = questions.filter(qq => qq.repeat_group === q.repeat_group);
                return renderRepeatGroup(q.repeat_group, groupQs, questions);
            }
            normalIdx++;
            return (
                <Card key={q.id} size="small" style={{marginBottom:12,
                    borderLeft:`3px solid ${answers[q.id]!==undefined?'#16a34a':'#e2e8f0'}`}}>
                    <div style={{marginBottom:10}}>
                        <span style={{fontWeight:600,fontSize:14}}>{normalIdx}. {q.label}</span>
                        {q.required && <Tag color="red" style={{marginLeft:8,fontSize:10}}>Required</Tag>}
                        {q.helper_text &&
                            <div style={{fontSize:12,color:'#94a3b8',marginTop:4}}>{q.helper_text}</div>}
                    </div>
                    {renderQ(q)}
                </Card>
            );
        }).filter(Boolean);
    };

    // ── Loading / error states ────────────────────────────────────────────────
    if (loading) return (
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'100vh'}}>
            <Spin size="large"/>
        </div>
    );
    if (error) return (
        <div style={{minHeight:'100vh',background:'#f0f4f8',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{maxWidth:500,padding:32,background:'#fff',borderRadius:16,
                boxShadow:'0 4px 24px rgba(0,0,0,0.08)',textAlign:'center'}}>
                <div style={{fontSize:48,marginBottom:16}}>
                    {error.includes('closed')?'🔒':error.includes('started')?'⏳':'❌'}
                </div>
                <h2 style={{color:'#0d2137',margin:'0 0 8px'}}>
                    {error.includes('closed')?'Survey Closed':error.includes('started')?'Not Yet Open':'Access Error'}
                </h2>
                <p style={{color:'#64748b'}}>{error}</p>
                <div style={{marginTop:20,fontSize:12,color:'#94a3b8'}}>Ministry of Health · Kingdom of Eswatini</div>
            </div>
        </div>
    );
    if (!data) return null;

    const { survey, facilities, collector, requires_owner_submit } = data;
    const allSections = survey.sections?.length ? survey.sections : [{ id: null, title: 'Questions' }];
    const sections = (collector.section_ids && collector.section_ids.length) ? allSections.filter(s => collector.section_ids.map(Number).includes(Number(s.id))) : allSections;
    const currentSec = sections[section];
    const currentQuestions = (survey.questions || []).filter(q =>
        currentSec.id ? (!q.section_id || String(q.section_id) === String(currentSec.id)) : true
    );
    const isLast   = section >= sections.length - 1;
    const deadline = survey.period_end
        ? new Date(survey.period_end).toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'})
        : null;

    const visibleAllQ = (survey.questions || []).filter(q => shouldShow(q));
    const totalQ      = visibleAllQ.length || 0;
    const answered    = visibleAllQ.filter(q =>
        answers[q.id] !== undefined && answers[q.id] !== '' && answers[q.id] !== null).length;

    // ── Submitted view ────────────────────────────────────────────────────────
    if (submitted) return (
        <div style={{minHeight:'100vh',background:'#f0f4f8'}}>
            <div style={{background:'linear-gradient(135deg,#0d2137,#1e3a5f)',boxShadow:'0 4px 20px rgba(0,0,0,0.3)'}}>
                <div style={{background:'rgba(0,0,0,0.2)',padding:'6px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.7)',letterSpacing:1.5,textTransform:'uppercase'}}>Kingdom of Eswatini</div>
                    <img src={flagImg} alt="Flag" style={{height:20,borderRadius:2}}/>
                </div>
                <div style={{padding:'16px 40px',display:'flex',alignItems:'center',gap:16}}>
                    <img src={coaImg} alt="Coat of Arms" style={{height:60,filter:'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',flexShrink:0}}/>
                    <div style={{flex:1}}>
                        <h2 style={{color:'#fff',margin:0,fontSize:18}}>{survey.title}</h2>
                        <div style={{color:'rgba(255,255,255,0.7)',fontSize:12,marginTop:2}}>Collector: {collector.name}</div>
                    </div>
                </div>
            </div>
            <div style={{maxWidth:700,margin:'0 auto',padding:'24px 16px'}}>
                <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,0.06)',marginBottom:16}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                        <div style={{fontWeight:700,fontSize:16,color:'#0d2137'}}>📊 My Submissions</div>
                        <Tag color="blue">{submissions.length} facilit{submissions.length===1?'y':'ies'} collected</Tag>
                    </div>
                    {submissions.map((sub,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 12px',
                            background:'#f0fdf4',borderRadius:8,marginBottom:8,border:'1px solid #bbf7d0'}}>
                            <CheckCircleOutlined style={{color:'#16a34a',fontSize:18,flexShrink:0}}/>
                            <div style={{flex:1}}>
                                <div style={{fontWeight:700,color:'#0d2137'}}>{sub.facility_name}</div>
                                <div style={{fontSize:11,color:'#888'}}>{sub.submitted_at}</div>
                            </div>
                            <Tag color="green">Submitted</Tag>
                        </div>
                    ))}
                </div>
                {facilities.filter(f=>!submissions.find(s=>String(s.facility_id)===String(f.id))).length > 0 && (
                    <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                        <div style={{fontWeight:700,fontSize:15,color:'#0d2137',marginBottom:12}}>➕ Collect for Another Facility</div>
                        <Select showSearch value={nextFacility} onChange={setNextFacility}
                            optionFilterProp="children"
                            placeholder="Select next facility..." style={{width:'100%',marginBottom:12}}>
                            {facilities
                                .filter(f=>!submissions.find(s=>String(s.facility_id)===String(f.id)))
                                .map(f => {
                                    const status = f.submission_status;
                                    const icon = status==='submitted'?'✅':status==='draft'?'⏳':'⬜';
                                    const sub = status==='submitted'?' ('+f.submitted_by_name+')':status==='draft'?' (In progress)':'';
                                    return <Option key={f.id} value={f.id}>{icon} {f.business_name} — {f.located_region}{sub}</Option>;
                                })}
                        </Select>
                        <Button type="primary" disabled={!nextFacility} onClick={()=>{
                            setFacilityId(nextFacility);
                            setAnswers({});
                            setRepeatCounts({});
                            setSection(0);
                            setSubmitted(false);
                            setNextFacility(null);
                        }}>Start Collection →</Button>
                    </div>
                )}
                {facilities.filter(f=>!submissions.find(s=>String(s.facility_id)===String(f.id))).length === 0 && (
                    <div style={{textAlign:'center',padding:24,background:'#f0fdf4',borderRadius:12,border:'2px solid #bbf7d0'}}>
                        <div style={{fontSize:40,marginBottom:8}}>🎉</div>
                        <div style={{fontWeight:700,color:'#16a34a',fontSize:16}}>All facilities collected!</div>
                        <div style={{color:'#888',fontSize:13,marginTop:4}}>You have submitted data for all your assigned facilities.</div>
                    </div>
                )}
            </div>
        </div>
    );

    // ── Main form ─────────────────────────────────────────────────────────────
    return (
        <div style={{minHeight:'100vh',background:'#f0f4f8'}}>
            <div style={{background:'linear-gradient(135deg,#0d2137,#1e3a5f,#1a4f7a)',boxShadow:'0 4px 20px rgba(0,0,0,0.3)'}}>
                <div style={{background:'rgba(0,0,0,0.2)',padding:'6px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.7)',letterSpacing:1.5,textTransform:'uppercase'}}>Kingdom of Eswatini</div>
                    <img src={flagImg} alt="Flag" style={{height:20,borderRadius:2}}/>
                </div>
                <div style={{padding:'20px 40px',display:'flex',alignItems:'center',gap:20}}>
                    <img src={coaImg} alt="Coat of Arms" style={{height:80,filter:'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',flexShrink:0}}/>
                    <div style={{flex:1,textAlign:'center'}}>
                        <div style={{fontSize:12,color:'rgba(255,255,255,0.7)',fontWeight:600,marginBottom:4}}>Ministry of Health</div>
                        <h1 style={{color:'#fff',margin:'0 0 6px',fontSize:22,fontWeight:800}}>{survey.title}</h1>
                        {survey.program_name && <div style={{display:'inline-block',background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',borderRadius:20,padding:'3px 14px',fontSize:12,color:'#fff',marginBottom:4}}>📋 {survey.program_name}</div>}
                        <div style={{color:'rgba(255,255,255,0.7)',fontSize:12,marginTop:4}}>Welcome, <strong style={{color:'#fff'}}>{collector.name}</strong></div>
                    </div>
                    <img src={coaImg} alt="" style={{height:80,filter:'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',flexShrink:0,opacity:0.6}}/>
                </div>
                <div style={{background:'rgba(0,0,0,0.15)',padding:'8px 40px',display:'flex',gap:20,flexWrap:'wrap',alignItems:'center'}}>
                    {deadline && <div style={{fontSize:12,color:'rgba(255,255,255,0.8)'}}>⏰ Deadline: <strong style={{color:'#facc15'}}>{deadline}</strong></div>}
                    <div style={{fontSize:12,color:'rgba(255,255,255,0.8)'}}>📝 {totalQ} questions</div>
                    <div style={{fontSize:12,color:'rgba(255,255,255,0.8)'}}>📍 {facilities.length} facilit{facilities.length===1?'y':'ies'} assigned</div>
                </div>
            </div>

            <div style={{width:'100%',padding:'20px 32px'}}>
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
                    <Progress percent={totalQ?Math.round(answered/totalQ*100):0} size="small" style={{flex:1}}/>
                    <span style={{fontSize:12,color:'#888',whiteSpace:'nowrap'}}>{answered}/{totalQ}</span>
                </div>

                {/* Facility selector */}
                {section === 0 && (
                    <Card size="small" style={{marginBottom:16,borderLeft:'4px solid #1890ff'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,padding:'8px 12px',background:'#f8fafc',borderRadius:6}}>
                            <div style={{fontSize:13,color:'#64748b'}}>👤 Collecting as:</div>
                            <div style={{fontWeight:700,color:'#0d2137'}}>{collector.name}</div>
                            <div style={{fontSize:12,color:'#94a3b8'}}>({collector.email})</div>
                        </div>
                        <div style={{fontWeight:700,marginBottom:8}}>Select Facility <span style={{color:'red'}}>*</span></div>
                        <Select showSearch value={facilityId} optionFilterProp="children"
                            onChange={v => {
                                const fac = facilities.find(f=>String(f.id)===String(v));
                                if (fac?.submission_status === 'submitted') {
                                    if (!window.confirm(`${fac.business_name} was already submitted by ${fac.submitted_by_name}. Do you want to submit another response?`)) return;
                                }
                                setFacilityId(v);
                            }}
                            placeholder="Select your facility" style={{width:'100%'}}>
                            {facilities.map(f => {
                                const status = f.submission_status;
                                const icon = status==='submitted'?'✅':status==='draft'?'⏳':'⬜';
                                const sub = status==='submitted'?' ('+f.submitted_by_name+')':status==='draft'?' (In progress)':'';
                                return <Option key={f.id} value={f.id}>{icon} {f.business_name} — {f.located_region}{sub}</Option>;
                            })}
                        </Select>
                        <div style={{display:'flex',gap:12,marginTop:6,fontSize:11,color:'#888'}}>
                            <span>⬜ Not started</span><span>⏳ In progress</span><span>✅ Submitted</span>
                        </div>
                        {facilities.length === 0 && <div style={{fontSize:12,color:'#dc2626',marginTop:6}}>No facilities assigned. Contact your supervisor.</div>}
                    </Card>
                )}

                {/* Section tabs */}
                {sections.length > 1 && (
                    <div style={{display:'flex',gap:4,marginBottom:16,overflowX:'auto'}}>
                        {sections.map((sec,i)=>(
                            <button key={sec.id||i} onClick={()=>setSection(i)}
                                style={{padding:'6px 14px',borderRadius:6,
                                    border:'1px solid '+(i===section?'#1890ff':'#e5e7eb'),
                                    background:i===section?'#1890ff':'#fff',
                                    color:i===section?'#fff':'#555',
                                    cursor:'pointer',fontSize:12,fontWeight:600,flexShrink:0,
                                    // Highlight repeat group sections
                                    ...(sec.is_repeat?{borderColor:'#7c3aed',color:i===section?'#fff':'#7c3aed',background:i===section?'#7c3aed':'#faf5ff'}:{})}}>
                                {sec.is_repeat ? '👥 ' : ''}{sec.title}
                            </button>
                        ))}
                    </div>
                )}

                {/* Questions */}
                {renderQuestionList(currentQuestions)}

                {/* Navigation */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:20,gap:8,flexWrap:'wrap'}}>
                    <div style={{display:'flex',gap:8}}>
                        <Button icon={<SaveOutlined/>} loading={saving} onClick={saveDraft}
                            style={{borderColor:'#d97706',color:'#d97706'}}>Save Draft</Button>
                        {section > 0 && <Button onClick={()=>setSection(s=>s-1)}>← Back</Button>}
                    </div>
                    <div>
                        {!isLast
                            ? <Button type="primary" onClick={()=>setSection(s=>s+1)}>Next →</Button>
                            : (requires_owner_submit ? <span style={{color:'#888',fontSize:12,fontStyle:'italic'}}>This tool requires the survey owner to do the final submission once every section is complete.</span> : <Button type="primary" size="large" icon={<SendOutlined/>}
                                loading={submitting} onClick={submit}
                                style={{background:'#16a34a',borderColor:'#16a34a',padding:'0 28px'}}>
                                Submit Survey
                              </Button>)
                        }
                    </div>
                </div>

                <div style={{textAlign:'center',marginTop:24,padding:'16px 0',borderTop:'1px solid #e2e8f0'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,marginBottom:4}}>
                        <img src={coaImg} alt="" style={{height:28,opacity:0.5}}/>
                        <span style={{fontSize:11,color:'#94a3b8'}}>Ministry of Health · Kingdom of Eswatini</span>
                        <img src={flagImg} alt="" style={{height:14,opacity:0.5,borderRadius:2}}/>
                    </div>
                    <div style={{fontSize:11,color:'#cbd5e1'}}>Powered by MoH Assessment Platform</div>
                </div>
            </div>
        </div>
    );
}




